import { CSSObject } from '@emotion/react'
import { useReducer, useEffect, MouseEventHandler } from 'react'
import { getBackboneWindow } from '@opallabs/brown-dwarf'
import { AssetReference, ImageTransforms } from '@opallabs/cassini'
import { ImageBase, MediaBase } from './AssetBase'

type StateProps = {
  error?: boolean
  fakePercent: number
  loaded?: boolean
}

enum ActionTypes {
  error = 'ERROR',
  loaded = 'LOADED',
  loading = 'LOADING',
  reset = 'RESET',
}

type ActionProps = {
  type: ActionTypes
}

export interface Props {
  assetReference: AssetReference | null
  autoPlay?: boolean
  deletable?: boolean
  disableCoverAsset?: boolean
  disableMediaControls?: boolean
  downloadable?: boolean
  getDuration?: (duration: number) => void
  hasAssetOptions?: boolean
  hasAssetOverlay?: boolean
  readOnly?: boolean
  isHovered?: boolean
  imageFit?: string
  infoOverlayStyles?:
    | (() => CSSObject)
    | CSSObject
    | Array<CSSObject | undefined>
  onDelete?: MouseEventHandler<HTMLButtonElement>
  onEmbiggen?: MouseEventHandler<HTMLDivElement>
  onRemove?: MouseEventHandler<HTMLButtonElement>
  overrideStyles?: {
    asset?: CSSObject | Array<CSSObject | undefined>
    assetBase?: CSSObject | Array<CSSObject | undefined>
  }
  placeholder?: boolean
  preview?: boolean
  removeable?: boolean
  thumbnail?: boolean
  transforms?: ImageTransforms
  updateCaption?: (value: string, assetId: number) => void
  updateCoverAsset?: (value: string) => void
  videoLoop?: boolean
}

const getAssetBase = (asset: AssetReference | null) => {
  if (!asset) return ImageBase

  switch (asset.asset_type) {
    case 'video':
      return MediaBase
    case 'audio':
      return MediaBase
    case 'image':
    default:
      return ImageBase
  }
}

const updateFakePercent = (currentPercent: number) => {
  if (currentPercent >= 95) return currentPercent

  return currentPercent + 5
}

const reducer = (state: StateProps, action: ActionProps) => {
  switch (action.type) {
    case ActionTypes.loaded:
      return { ...state, loaded: true }
    case ActionTypes.error:
      return { ...state, loaded: true, error: true }
    case ActionTypes.loading:
      return {
        ...state,
        fakePercent: updateFakePercent(state.fakePercent),
        loaded: false,
      }
    case ActionTypes.reset:
      return { ...state, fakePercent: 10, loaded: false, error: false }
    default:
      throw new Error()
  }
}

const Asset: React.FC<Props> = ({
  assetReference,
  autoPlay = false,
  deletable,
  disableCoverAsset = false,
  disableMediaControls = false,
  downloadable = true,
  getDuration = () => null,
  hasAssetOptions = false,
  hasAssetOverlay = true,
  readOnly,
  isHovered = false,
  imageFit = 'cover',
  infoOverlayStyles,
  onDelete,
  onEmbiggen,
  onRemove,
  overrideStyles = {},
  placeholder,
  preview,
  removeable,
  thumbnail,
  transforms,
  updateCaption = () => null,
  updateCoverAsset = () => null,
  videoLoop = false,
}) => {
  const asset =
    assetReference && assetReference.cover_asset_reference && !disableCoverAsset
      ? assetReference.cover_asset_reference
      : assetReference
  const hasAsset = asset && asset.uuid
  const hasCoverAsset = assetReference && assetReference.cover_asset_reference
  const assetIncomplete = asset && !asset.uuid
  const [state, dispatch] = useReducer(reducer, {
    fakePercent: 10,
    loaded: false,
  })
  const AssetBase = getAssetBase(assetReference)
  const isLimitedUser =
    getBackboneWindow()?.Opal?.State?.this_user_role === 'limited'

  useEffect(() => {
    let interval

    if (assetIncomplete) {
      interval = setInterval(
        () => dispatch({ type: ActionTypes.loading }),
        1000
      )
    }

    if (hasAsset && interval) {
      clearInterval(interval)
    }

    if (!asset) {
      dispatch({ type: ActionTypes.reset })
    }
  }, [asset, assetIncomplete, hasAsset, dispatch])

  return (
    <AssetBase
      assetReference={assetReference}
      autoPlay={autoPlay}
      coverAsset={hasCoverAsset ? assetReference?.cover_asset_reference : null}
      disableCoverAsset={disableCoverAsset}
      deletable={deletable}
      getDuration={getDuration}
      downloadable={downloadable}
      hasAssetOverlay={isLimitedUser ? true : hasAssetOverlay}
      imageFit={imageFit}
      infoOverlayStyles={infoOverlayStyles}
      hasAssetOptions={hasAssetOptions}
      isHovered={isHovered}
      isLimitedUser={isLimitedUser}
      disableMediaControls={disableMediaControls}
      onDelete={onDelete}
      onEmbiggen={onEmbiggen}
      onRemove={onRemove}
      overrideStyles={overrideStyles}
      placeholder={placeholder}
      readOnly={isLimitedUser ? false : readOnly}
      removeable={isLimitedUser ? false : removeable}
      loaded={state.loaded}
      onLoad={() => dispatch({ type: ActionTypes.loaded })}
      onError={() => dispatch({ type: ActionTypes.error })}
      percent={assetReference?.processing_percent ?? state.fakePercent}
      preview={preview}
      updateCaption={updateCaption}
      updateCoverAsset={updateCoverAsset}
      thumbnail={thumbnail}
      transforms={transforms}
      videoLoop={videoLoop}
    />
  )
}

export default Asset
